import React from 'react';
import { CheckCircle2, XCircle, ArrowRight, Lightbulb } from 'lucide-react';

export const STAGE_HINTS = {
  graft: {
    title: 'Grafting Day (Day 0)',
    summary: 'Move 12–24 hour old larvae into cell cups and give them to a well-fed, queenless cell starter.',
    dos: [
      'Pick the youngest larvae you can see, they should be barely larger than an egg and sitting in plenty of royal jelly',
      'Work in warm, humid conditions out of direct sun and wind (a damp towel over the frame helps)',
      'Prime cell cups with a little royal jelly or water to help acceptance',
      'Make sure the starter is packed with young nurse bees and has fresh pollen and nectar or 1:1 syrup'
    ],
    donts: [
      'Don\'t let larvae dry out, so keep each frame out of the hive for less than 10 minutes',
      'Don\'t roll or flip the larva, it must stay on the same side it was lying on',
      'Don\'t graft into a starter that hasn\'t been queenless for at least 2–3 hours'
    ],
    next: 'Tomorrow, check acceptance and move the cell bar to the finisher.'
  },
  finisher: {
    title: 'Move to Finisher (Day 1)',
    summary: 'About 24 hours after grafting, check which cells were accepted and move the bar into a queenright finisher above the excluder.',
    dos: [
      'Count the accepted cells: they will be drawn out and well stocked with royal jelly',
      'Put the cell bar in the middle of the finisher, between frames of open brood',
      'Keep feeding the finisher so the bees keep building cells'
    ],
    donts: [
      'Don\'t let the cell bar cool down while you move it',
      'Don\'t leave an unchecked frame of open brood that could hold a rival queen cell'
    ],
    next: 'Leave the cells undisturbed until they are capped on Day 9.'
  },
  capped: {
    title: 'Cells Capped (Day 9)',
    summary: 'The queen cells are sealed and the pupae are developing. They are now at their most fragile.',
    dos: [
      'Count the capped cells and record them on the batch',
      'Get your mating nucs or incubator ready for transfer tomorrow',
      'Keep the finisher well fed so it doesn\'t tear cells down'
    ],
    donts: [
      'Don\'t jar, tilt or shake the cell bar, because the pupae can be damaged easily at this stage',
      'Don\'t leave cells in direct sunlight or the cold, not even for a minute or two',
      'Don\'t open the finisher more often than you need to'
    ],
    next: 'Transfer the cells to nucs or an incubator tomorrow (Day 10), before any queen emerges.'
  },
  transfer: {
    title: 'Transfer to Nucs (Day 10)',
    summary: 'Move the ripe cells into queenless mating nucs or an incubator about 2 days before emergence.',
    dos: [
      'Carry cells upright in a warm, padded cell carrier (around 32–35°C)',
      'Press each cell gently into comb near brood, with the tip pointing down',
      'Make sure every nuc has been queenless for at least 24 hours and has plenty of young bees',
      'Record which nuc each cell went into'
    ],
    donts: [
      'Don\'t leave cells in the finisher past Day 11, because the first queen to emerge will kill the rest',
      'Don\'t squeeze or bend the cell tip',
      'Don\'t put a cell into a nuc that already has a queen or queen cells'
    ],
    next: 'Queens emerge in about 6 days, so leave the nucs undisturbed.'
  },
  emerge: {
    title: 'Queen Emergence (Day 16)',
    summary: 'Virgin queens chew out of their cells. They need calm and time to mature.',
    dos: [
      'Check from a distance that the cells have opened neatly at the tip (side holes mean a rival tore it down)',
      'Feed the nucs lightly if there\'s a dearth',
      'Mark down any cells that failed'
    ],
    donts: [
      'Don\'t open the nucs or go looking for the virgin queen, because she is easily lost or balled',
      'Don\'t move the nucs once the queens have emerged',
      'Don\'t mark or clip virgin queens'
    ],
    next: 'Mating flights start in about a week, so keep disturbance to a minimum.'
  },
  mating: {
    title: 'Mating Flights (Day 22)',
    summary: 'Virgins take orientation and mating flights on warm, calm afternoons.',
    dos: [
      'Make sure there are plenty of drones in the area (look for drone brood in nearby colonies)',
      'Make nuc entrances easy to spot, for example with different colours or markings',
      'Watch the weather, because a cold or wet spell can hold mating back'
    ],
    donts: [
      'Don\'t open the nucs between 11am and 4pm, when the queens are likely to be out flying',
      'Don\'t shift nuc positions, because a returning queen can end up in the wrong box',
      'Don\'t panic if there are no eggs yet, as mating can take up to 3 weeks'
    ],
    next: 'Check for eggs and a laying queen around Day 30.'
  },
  check: {
    title: 'Laying Check (Day 30)',
    summary: 'Confirm that each queen is mated and laying well before you use or sell her.',
    dos: [
      'Look for one egg per cell, laid in the centre of the cell, in a solid pattern',
      'Mark and record each laying queen, then assign her to a hive or a sale',
      'Assess her temperament and how the brood looks'
    ],
    donts: [
      'Don\'t keep queens that lay several eggs per cell or only drone brood (scattered capped brood with domed cappings)',
      'Don\'t introduce her to a full colony until she has been laying for at least a week'
    ],
    next: 'Move laying queens to hives with "Assign Queen", then archive the batch once it\'s complete.'
  }
};

export const QueenStageHints = ({ stageKey, daysUntil }) => {
  const hint = STAGE_HINTS[stageKey];
  if (!hint) return null;
  const when = daysUntil === 0 ? 'Today' : daysUntil > 0 ? `In ${daysUntil} day${daysUntil === 1 ? '' : 's'}` : 'Done';
  return (
    <div data-testid={`stage-hint-${stageKey}`} className="mt-4 rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-4">
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="font-semibold text-amber-900 flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-amber-600" /> {hint.title}
        </h4>
        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 whitespace-nowrap">{when}</span>
      </div>
      <p className="text-sm text-stone-600 mb-3">{hint.summary}</p>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-1">Do</p>
          <ul className="space-y-1">
            {hint.dos.map(d => (
              <li key={d} className="text-sm text-stone-700 flex gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />{d}</li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">Don't</p>
          <ul className="space-y-1">
            {hint.donts.map(d => (
              <li key={d} className="text-sm text-stone-700 flex gap-2"><XCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />{d}</li>
            ))}
          </ul>
        </div>
      </div>
      <p className="mt-3 text-sm font-medium text-amber-800 flex items-center gap-2 border-t border-amber-100 pt-2">
        <ArrowRight className="h-4 w-4" /> Next: {hint.next}
      </p>
    </div>
  );
};
